/**
 * The video codec order every one of our clients offers (spec §5.5): exactly one H.264, in packetization-mode 1, then
 * VP8 - and nothing else.
 *
 * Asterisk can't transcode video. If two legs of a call end up sharing no codec, there is no video in that direction and
 * nothing on the PBX can fix it, so all our clients agree on one order instead of leaving it to the browser. H.264 goes
 * first because phones encode it in hardware; VP8 is the software fallback. Chrome's other H.264 profiles, VP9, AV1 and
 * H.265 are dropped, since Asterisk can't carry them reliably.
 *
 * RTX is kept for the two codecs we keep. RED and FEC are dropped - Asterisk ignores them anyway.
 */

/** The H.264 profile to prefer: Constrained Baseline, level 3.1 - the one every hardware encoder does */
const PREFERRED_H264_PROFILE = '42e01f'

/**
 * Order a list of video codec capabilities for RTCRtpTransceiver.setCodecPreferences
 * @param caps The codecs, normally RTCRtpReceiver.getCapabilities('video').codecs
 * @returns One H.264 with packetization-mode=1 (preferring profile-level-id 42e01f), then VP8, then the rtx entries for
 * those two. Without H.264: VP8 and its rtx. Empty if neither is available - which setCodecPreferences takes as "reset".
 */
export function orderVideoCodecs(caps: RTCRtpCodec[]): RTCRtpCodec[] {
	const h264 = caps.filter(codec => isMime(codec, 'video/h264') && fmtp(codec)['packetization-mode'] === '1')
	const chosenH264 = h264.find(codec => fmtp(codec)['profile-level-id']?.toLowerCase() === PREFERRED_H264_PROFILE) ?? h264[0]
	const vp8 = caps.find(codec => isMime(codec, 'video/vp8'))
	const chosen = [chosenH264, vp8].filter(codec => codec !== undefined)

	if (chosen.length === 0) { return [] }

	const rtx = caps.filter(codec => isMime(codec, 'video/rtx'))

	// With payload types (e.g. getParameters().codecs) each rtx entry names the codec it protects with apt=<payload type>
	const associatedRtx = chosen
		.map(codec => payloadTypeOf(codec))
		.map(payloadType => (payloadType === undefined ? undefined : rtx.find(entry => fmtp(entry).apt === String(payloadType))))
		.filter(entry => entry !== undefined)

	// RTCRtpReceiver.getCapabilities has no payload types, so Chrome lists rtx once, without apt, and applies it to every
	// codec kept. Dropping that entry would turn off retransmission altogether.
	const genericRtx = rtx.find(entry => fmtp(entry).apt === undefined)

	return [...chosen, ...associatedRtx, ...genericRtx ? [genericRtx] : []]
}

function isMime(codec: RTCRtpCodec, mimeType: string) {
	return codec.mimeType.toLowerCase() === mimeType
}

/** A codec's fmtp line as key/value pairs, keys in lower case */
function fmtp(codec: RTCRtpCodec): Record<string, string | undefined> {
	const result: Record<string, string | undefined> = {}

	for (const part of (codec.sdpFmtpLine ?? '').split(';')) {
		const [key, ...value] = part.split('=')

		if (key?.trim()) { result[key.trim().toLowerCase()] = value.join('=').trim() }
	}
	return result
}

/** The payload type, when the list carries one (RTCRtpCodecParameters) - capabilities don't */
function payloadTypeOf(codec: RTCRtpCodec): number | undefined {
	return (codec as Partial<RTCRtpCodecParameters>).payloadType
}
